import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Logo from '../components/Logo';
import SEO from '../components/SEO';
import { MapPin, ChevronRight } from 'lucide-react';

// Images de bureaux pour le carrousel
const parisImages = [
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=500&fit=crop',
  'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&h=500&fit=crop',
  'https://images.unsplash.com/photo-1604328698692-f76ea9498e76?w=800&h=500&fit=crop',
  'https://images.unsplash.com/photo-1564069114553-7215e1ff1890?w=800&h=500&fit=crop',
];

const genevaImages = [
  'https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=800&h=500&fit=crop',
  'https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?w=800&h=500&fit=crop',
  'https://images.unsplash.com/photo-1574958269340-fa927503f3dd?w=800&h=500&fit=crop',
  'https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=800&h=500&fit=crop',
];

// Hook pour animations au scroll
const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1 });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return [ref, isInView];
};

// Composant pour sections animées
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transform transition-all duration-700 ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)'
      }}
    >
      {children}
    </div>
  );
};

const About = () => {
  const { t, i18n } = useTranslation();
  
  // SEO data with translations
  const seoData = {
    fr: {
      title: 'Qui sommes-nous - FOMOW | Agence Performance Commerciale Paris & Genève',
      description: 'Découvrez FOMOW : experts de la vente et de la croissance B2B, présents à Paris et Genève. Nos valeurs ACTE guident notre approche pour accélérer vos revenus.'
    },
    en: {
      title: 'About Us - FOMOW | Commercial Performance Agency Paris & Geneva',
      description: 'Discover FOMOW: B2B sales and growth experts, present in Paris and Geneva. Our ACTE values guide our approach to accelerate your revenue.'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.fr;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": currentSeo.title,
    "description": currentSeo.description,
    "url": "https://www.fomow.fr/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "FOMOW",
      "foundingLocation": ["Paris", "Geneva"],
      "slogan": "Vendre mieux, plus vite et plus cher"
    }
  };

  // États pour les carrousels d'images
  const [parisImageIndex, setParisImageIndex] = useState(0);
  const [genevaImageIndex, setGenevaImageIndex] = useState(0);

  // Carrousel automatique
  useEffect(() => {
    const parisInterval = setInterval(() => {
      setParisImageIndex((prev) => (prev + 1) % parisImages.length);
    }, 4000);
    
    const genevaInterval = setInterval(() => {
      setGenevaImageIndex((prev) => (prev + 1) % genevaImages.length);
    }, 4500);

    return () => {
      clearInterval(parisInterval);
      clearInterval(genevaInterval);
    };
  }, []);

  // Valeurs ACTE
  const values = [
    {
      letter: 'A',
      titleKey: 'about.valueAgility',
      descKey: 'about.valueAgilityDesc',
    },
    {
      letter: 'C',
      titleKey: 'about.valueClarity',
      descKey: 'about.valueClarityDesc',
    },
    {
      letter: 'T',
      titleKey: 'about.valueTransmission',
      descKey: 'about.valueTransmissionDesc',
    },
    {
      letter: 'E',
      titleKey: 'about.valueEngagement',
      descKey: 'about.valueEngagementDesc',
    },
  ];

  // Bureaux
  const offices = [
    {
      city: 'Paris',
      flag: '🇫🇷',
      locationKey: 'about.parisSaintLazare',
      addressKey: 'about.parisAddress',
      images: parisImages,
      currentIndex: parisImageIndex,
    },
    {
      city: 'Genève',
      flag: '🇨🇭',
      locationKey: 'about.genevaQuai',
      addressKey: 'about.genevaAddress',
      images: genevaImages,
      currentIndex: genevaImageIndex,
    },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        path="/about"
        structuredData={structuredData}
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 bg-gradient-to-b from-[#041C2C] to-[#0a2a3d] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#2A41E5]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#2A41E5]/5 rounded-full blur-2xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5]/20 border border-[#2A41E5]/30 mb-6">
              <span className="text-sm text-[#2A41E5] font-medium">{t('about.badge')}</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              {t('about.heroTitle')}<br />
              <span className="text-[#2A41E5]">{t('about.heroTitleHighlight')}</span>
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl leading-relaxed">
              {t('about.heroSubtitle')}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Notre Mission */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-4 block">{t('about.missionLabel')}</span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  {t('about.missionTitle')}
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  {t('about.missionText1')}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {t('about.missionText2')}
                </p>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={200}>
              <div className="bg-[#041C2C] rounded-2xl p-10 text-center">
                <Logo variant="white" height={60} />
                <p className="text-white/80 mt-6 text-lg leading-relaxed">
                  {t('about.missionQuote')}
                </p>
                <div className="mt-8 pt-8 border-t border-white/10 grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-3xl font-bold text-[#2A41E5]">6</div>
                    <div className="text-white/60 text-sm">{t('about.statsServices')}</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#2A41E5]">2</div>
                    <div className="text-white/60 text-sm">{t('about.statsBureaux')}</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-[#2A41E5]">100%</div>
                    <div className="text-white/60 text-sm">{t('about.statsEngages')}</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Nos Valeurs - L'esprit A.C.T.E. */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-6">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900">
                {t('about.valuesTitle')}
              </h2>
            </div>
            <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-16">
              {t('about.valuesSubtitle')}
            </p>
          </AnimatedSection>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Illustration à gauche */}
            <AnimatedSection>
              <div className="sticky top-32">
                <img 
                  src="/images/team-values-illustration-blue.png" 
                  alt="Illustration des valeurs FOMOW - équipe dynamique" 
                  className="w-full max-w-lg mx-auto"
                />
              </div>
            </AnimatedSection>
            
            {/* Valeurs à droite */}
            <div className="space-y-6">
              {values.map((value, idx) => (
                <AnimatedSection key={idx} delay={idx * 150}>
                  <div className="flex items-start gap-6 group">
                    {/* Bullet point */}
                    <div className="w-3 h-3 rounded-sm bg-[#041C2C] mt-3 flex-shrink-0" />
                    
                    {/* Lettre */}
                    <div className="text-4xl md:text-5xl font-bold text-[#2A41E5] flex-shrink-0 w-12">
                      {value.letter}
                    </div>
                    
                    {/* Contenu */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 uppercase tracking-wide mb-1">
                        {t(value.titleKey)}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {t(value.descKey)}
                      </p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Nos Bureaux */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-4">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider">{t('about.officesLabel')}</span>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 text-center">
              {t('about.officesTitle')}
            </h2>
            <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-16">
              {t('about.officesSubtitle')}
            </p>
          </AnimatedSection>
          
          <div className="grid md:grid-cols-2 gap-8">
            {offices.map((office, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="bg-[#F5F5F5] rounded-2xl overflow-hidden border border-gray-100">
                  {/* Images carrousel */}
                  <div className="relative h-64 overflow-hidden">
                    {office.images.map((img, imgIdx) => (
                      <img 
                        key={imgIdx}
                        src={img} 
                        alt={`Bureau FOMOW ${office.city} ${imgIdx + 1}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                          imgIdx === office.currentIndex ? 'opacity-100' : 'opacity-0'
                        }`}
                      />
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <h3 className="text-2xl font-bold text-white">{office.city}</h3>
                      <p className="text-white/80">{t(office.locationKey)}</p>
                    </div>
                    {/* Indicateurs de slide */}
                    <div className="absolute bottom-4 right-4 flex gap-1.5">
                      {office.images.map((_, dotIdx) => (
                        <div 
                          key={dotIdx}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            dotIdx === office.currentIndex ? 'bg-white w-4' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-gray-600">
                      <MapPin className="w-5 h-5 text-[#2A41E5]" />
                      <span>{t(office.addressKey)}</span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
              {t('about.ctaTitle')}
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              {t('about.ctaSubtitle')}
            </p>
            <a
              href="https://meetings-eu1.hubspot.com/younes8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              {t('common.bookCall')}
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">{t('footer.copyright')} {t('common.allRightsReserved')}.</p>
        </div>
      </footer>
    </div>
  );
};

export default About;
